import React, { useState } from "react";
import { Card, Tag, Space, Typography, Button, Tooltip, Divider } from "antd";
import { ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, DownOutlined, RightOutlined, MailOutlined, EyeOutlined, DiffOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ReactMarkdown from "react-markdown";
import EmailViewer from "../common/EmailViewer";
import FlightComparisonViewer from "./FlightComparisonViewer";
import { getFlightEmailContent } from "../../api/mailApi";
 
const { Text } = Typography;

// 业务分类配置（与FlightHistoryTimeline保持一致）
const BUSINESS_CATEGORIES = {
    flight_changes: { label: "航班变更", color: "#1890ff" },
    permits: { label: "飞行许可", color: "#52c41a" },
    fuel: { label: "燃油保障", color: "#fa8c16" },
    catering: { label: "航食配餐", color: "#eb2f96" },
    crew_services: { label: "机组服务", color: "#722ed1" },
    notifications: { label: "通知沟通", color: "#13c2c2" },
};

// 获取业务分类
const getBusinessCategory = (eventType) => {
    if (eventType.includes("FLIGHT_ATTRIBUTE") || eventType.includes("STATUS_CHANGED") || eventType.includes("TIME_UPDATED") || eventType.includes("MVT")) {
        return "flight_changes";
    }
    if (eventType.includes("PERMIT")) {
        return "permits";
    }
    if (eventType.includes("FUEL")) {
        return "fuel";
    }
    if (eventType.includes("CATERING")) {
        return "catering";
    }
    if (eventType.includes("CREW") || eventType.includes("HOTAC")) {
        return "crew_services";
    }
    if (eventType.includes("EMAIL")) {
        return "notifications";
    }
    return "notifications";
};

// 获取状态图标
const getStatusIcon = (eventType, newValue) => {
    if (eventType === "EMAIL_SENT") {
        return <MailOutlined style={{ color: "#52c41a" }} />;
    }
    if (eventType.includes("_CREATED")) {
        return <InfoCircleOutlined style={{ color: "#1890ff" }} />;
    }
    if (eventType.includes("_UPDATED")) {
        if (newValue === "CONFIRMED" || newValue === "COMPLETED") {
            return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
        }
        if (newValue === "PENDING" || newValue === "PROCESSING") {
            return <ClockCircleOutlined style={{ color: "#fa8c16" }} />;
        }
        if (newValue === "CANCELLED" || newValue === "FAILED") {
            return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
        }
    }
    return <InfoCircleOutlined style={{ color: "#8c8c8c" }} />;
};

// 从事件描述中提取关键信息
const extractEventSummary = (event) => {
    const { event_description, event_type, field_name, previous_value, new_value, additional_info } = event;

    // 特殊处理EMAIL_SENT事件
    if (event_type === "EMAIL_SENT") {
        // 尝试从additional_info中获取邮件主题
        const emailSubject = additional_info?.email_data?.subject;
        if (emailSubject) {
            return emailSubject.length > 50 ? emailSubject.substring(0, 50) + "..." : emailSubject;
        }

        // 从事件描述中提取邮件类型
        if (event_description) {
            const cleanDesc = event_description
                .replace(/#{1,3}\s*/g, "")
                .replace(/\*\*/g, "")
                .replace(/`([^`]+)`/g, "$1");
            const lines = cleanDesc.split("\n").filter((line) => line.trim());
            const summary = lines[0] || "";
            return summary.length > 50 ? summary.substring(0, 50) + "..." : summary;
        }

        return "发送业务邮件";
    }

    if (event_description) {
        // 移除Markdown格式，提取第一行作为摘要
        const cleanDesc = event_description
            .replace(/#{1,3}\s*/g, "")
            .replace(/\*\*/g, "")
            .replace(/`([^`]+)`/g, "$1");
        const lines = cleanDesc.split("\n").filter((line) => line.trim());
        const summary = lines[0] || "";
        return summary.length > 60 ? summary.substring(0, 60) + "..." : summary;
    }

    // 如果有字段变更信息
    if (field_name && previous_value && new_value) {
        const fieldDisplayNames = {
            status: "状态",
            std: "计划起飞时间",
            sta: "计划到达时间",
            etd: "预计起飞时间",
            eta: "预计到达时间",
            atd: "实际起飞时间",
            ata: "实际到达时间",
        };
        const displayName = fieldDisplayNames[field_name] || field_name;
        return `${displayName}: ${previous_value} → ${new_value}`;
    }

    // 默认事件类型显示
    const eventDisplayNames = {
        FUEL_REQUEST_CREATED: "创建燃油申请",
        FUEL_REQUEST_UPDATED: "更新燃油申请",
        PERMIT_REQUEST_CREATED: "创建飞行许可申请",
        PERMIT_REQUEST_UPDATED: "更新飞行许可申请",
        CATERING_ORDER_CREATED: "创建配餐订单",
        CATERING_ORDER_UPDATED: "更新配餐订单",
        FLIGHT_ATTRIBUTE_UPDATED: "更新航班属性",
        STATUS_CHANGED: "航班状态变更",
        TIME_UPDATED: "航班时间更新",
        OPERATION_FLIGHT_UPDATED_MVT: "MVT报文更新",
        EMAIL_SENT: "发送邮件",
    };

    return eventDisplayNames[event_type] || event_type;
};

/**
 * 简化的航班事件条目组件
 */
const FlightEventItem = ({ event, onRelatedObjectClick }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [emailViewerVisible, setEmailViewerVisible] = useState(false);
    const [selectedEmailData, setSelectedEmailData] = useState(null);
    const [comparisonVisible, setComparisonVisible] = useState(false);

    const summary = extractEventSummary(event);
    const icon = getStatusIcon(event.event_type, event.new_value);
    const category = getBusinessCategory(event.event_type);
    const categoryInfo = BUSINESS_CATEGORIES[category];
    const time = dayjs(event.created_at).format("MM-DD HH:mm");

    const handleRelatedObjectClick = () => {
        if (onRelatedObjectClick && event.related_business_id) {
            onRelatedObjectClick("business", event.related_business_id, event.related_business_title || `${event.related_business_type} #${event.related_business_id}`);
        }
    };

    // 处理查看邮件
    const handleViewEmail = async () => {
        if (!event.related_email_id || !event.flight_id) {
            console.error("[FlightEventItem] 缺少邮件ID或航班ID", {
                emailId: event.related_email_id,
                flightId: event.flight_id,
            });
            return;
        }

        try {
            console.log("[FlightEventItem] 开始获取邮件详细内容:", {
                flightId: event.flight_id,
                emailId: event.related_email_id,
            });

            // 使用航班邮件API获取内容
            const response = await getFlightEmailContent(event.flight_id, event.related_email_id);
            const emailDetail = response?.data;

            if (!emailDetail) {
                console.error("[FlightEventItem] 未获取到邮件详细内容");
                return;
            }

            console.log("[FlightEventItem] 获取到邮件详细内容:", emailDetail);

            // 构造邮件数据对象，适配EmailViewer组件
            const emailData = {
                subject: emailDetail.subject || "无主题",
                email_subject: emailDetail.subject,
                email_type: emailDetail.business_link?.link_type || event.additional_info?.business_link_data?.link_type,
                recipients: emailDetail.to_address,
                to: emailDetail.to_address,
                cc: emailDetail.cc_address,
                bcc: emailDetail.bcc_address,
                send_time: emailDetail.sent_at || emailDetail.date || event.created_at,
                status: emailDetail.status,
                content_html: emailDetail.body_html,
                rendered_html: emailDetail.body_html,
                content: emailDetail.body_text,
                attachments: [],

                // 统计信息
                total_recipients: emailDetail.total_recipients,
                sent_count: emailDetail.sent_count,
                failed_count: emailDetail.failed_count,

                // 业务上下文
                business_context: emailDetail.business_link,

                // 群发邮件标识
                is_bulk_email: emailDetail.is_bulk_email,
                related_email_id: event.related_email_id,
            };

            console.log("[FlightEventItem] 构造的邮件数据:", emailData);
            setSelectedEmailData(emailData);
            setEmailViewerVisible(true);
        } catch (error) {
            console.error("[FlightEventItem] 获取邮件详细内容失败:", error);

            // 如果获取详细内容失败，使用事件数据中的基本信息
            const emailData = {
                subject: event.additional_info?.email_data?.subject || "无主题",
                email_subject: event.additional_info?.email_data?.subject,
                email_type: event.additional_info?.business_link_data?.link_type,
                recipients: event.additional_info?.email_data?.to_address,
                to: event.additional_info?.email_data?.to_address,
                cc: event.additional_info?.email_data?.cc_address,
                send_time: event.created_at,
                status: event.additional_info?.email_data?.status || "unknown",
                content_html: "邮件内容加载失败",
                rendered_html: "邮件内容加载失败",
                content: "邮件内容加载失败",
                attachments: [],
                error: error.message,

                // 群发邮件标识
                is_bulk_email: event.additional_info?.email_data?.is_bulk_email,
                related_email_id: event.related_email_id,
            };

            setSelectedEmailData(emailData);
            setEmailViewerVisible(true);
        }
    };

    // 关闭邮件查看器
    const handleCloseEmailViewer = () => {
        setEmailViewerVisible(false);
        setSelectedEmailData(null);
    };

    // 处理查看变更对比
    const handleViewComparison = () => {
        setComparisonVisible(true);
    };

    // 关闭变更对比
    const handleCloseComparison = () => {
        setComparisonVisible(false);
    };

    // 判断是否为航班变更类事件
    const isFlightChangeEvent = () => {
        const flightChangeTypes = ["FLIGHT_ATTRIBUTE_UPDATED", "PLAN_INITIATED", "STATUS_CHANGED", "TIME_UPDATED", "PLAN_REPLACEMENT", "OPERATION_FLIGHT_UPDATED_MVT"];
        return flightChangeTypes.includes(event.event_type);
    };

    return (
        <>
            <Card
                size="small"
                style={{
                    marginBottom: 8,
                    borderLeft: `4px solid ${categoryInfo.color}`,
                    backgroundColor: "#fafafa",
                }}
                styles={{ body: { padding: "12px 16px" } }}
            >
                {/* 主要信息行 */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                        {icon}
                        <Text strong style={{ fontSize: "14px" }}>
                            {summary}
                        </Text>
                        <Tag color={categoryInfo.color} size="small">
                            {categoryInfo.label}
                        </Tag>
                        {/* 邮件事件特殊处理：显示查看邮件按钮 */}
                        {event.event_type === "EMAIL_SENT" && event.related_email_id && (
                            <Button type="link" size="small" icon={<EyeOutlined />} onClick={handleViewEmail} style={{ padding: "0 4px", height: "auto" }}>
                                查看邮件
                            </Button>
                        )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        {time}
                    </Text>
                </div>

                {/* 邮件事件的特殊信息显示 */}
                {event.event_type === "EMAIL_SENT" && event.additional_info?.email_data && (
                    <div style={{ marginTop: 4, marginLeft: 24, fontSize: 12, color: "#666" }}>
                        {/* 邮件主题（可点击查看） */}
                        {event.additional_info.email_data.subject && (
                            <div style={{ marginBottom: 2 }}>
                                <Tooltip title="点击查看完整邮件内容">
                                    <span style={{ cursor: "pointer", color: "#1890ff", fontWeight: 500 }} onClick={handleViewEmail}>
                                        {event.additional_info.email_data.subject}
                                    </span>
                                </Tooltip>
                            </div>
                        )}

                        {/* 收件人信息 */}
                        {event.additional_info.email_data.to_address && <div style={{ marginBottom: 2 }}>收件人：{event.additional_info.email_data.to_address}</div>}
                        {event.additional_info.email_data.cc_address && <div style={{ marginBottom: 2 }}>抄送：{event.additional_info.email_data.cc_address}</div>}

                        {/* 邮件类型和状态 */}
                        <Space size="small">
                            {event.additional_info.business_link_data?.link_type && (
                                <Tag size="small" color="blue">
                                    {event.additional_info.business_link_data.link_type}
                                </Tag>
                            )}
                            {event.additional_info.email_data.is_bulk_email && (
                                <Tag size="small" color="purple">
                                    群发邮件
                                </Tag>
                            )}
                            {event.additional_info.email_data.status && (
                                <Tag size="small" color="green">
                                    {event.additional_info.email_data.status}
                                </Tag>
                            )}
                        </Space>
                    </div>
                )}

                {/* 业务上下文信息 */}
                {(event.operator || event.related_business_id) && event.event_type !== "EMAIL_SENT" && (
                    <div style={{ marginTop: 4, marginLeft: 24, fontSize: 12, color: "#666" }}>
                        <Space size="small">
                            {event.operator && <span>操作者: {event.operator}</span>}
                            {event.related_business_id && <span>#{event.related_business_id}</span>}
                        </Space>
                    </div>
                )}

                {/* 操作按钮区域 */}
                <div style={{ marginTop: 8, marginLeft: 24 }}>
                    <Space size="small">
                        {/* 航班变更事件：显示变更对比按钮 */}
                        {isFlightChangeEvent() && (
                            <Button type="link" size="small" icon={<DiffOutlined />} onClick={handleViewComparison} style={{ padding: 0, height: "auto", fontSize: 12 }}>
                                变更对比
                            </Button>
                        )}

                        <Divider type="vertical" />
                        {/* 关联对象链接 */}
                        {event.related_business_id && event.event_type !== "EMAIL_SENT" && (
                            <Button type="link" size="small" onClick={handleRelatedObjectClick} style={{ padding: 0, height: "auto", fontSize: 12 }}>
                                查看关联航班计划 →
                            </Button>
                        )}
                    </Space>
                </div>

                {/* 详细信息展开内容 */}
                {showDetails && event.event_description && (
                    <div
                        className="event-log-description markdown-content"
                        style={{
                            marginTop: 8,
                            marginLeft: 24,
                            padding: "8px 12px",
                            backgroundColor: "#fff",
                            borderRadius: 4,
                            border: "1px solid #f0f0f0",
                            fontSize: 12,
                        }}
                    >
                        <ReactMarkdown>{event.event_description}</ReactMarkdown>
                    </div>
                )}
            </Card>

            {/* 航班变更对比展示 */}
            {comparisonVisible && (
                <div style={{ marginTop: 8 }}>
                    <FlightComparisonViewer flightId={event.flight_id} visible={comparisonVisible} onClose={handleCloseComparison} />
                </div>
            )}

            {/* 邮件查看器 */}
            <EmailViewer visible={emailViewerVisible} onClose={handleCloseEmailViewer} emailData={selectedEmailData} title="邮件详情" width={900} showActions={false} />
        </>
    );
};

export default FlightEventItem;
